import type { Concept } from '@yuan-math/curriculum';

/**
 * The rest of Grade 3.
 *
 * `elementary.ts` carries the spine a Grade 3 student shares with Grades 4 and
 * 5 — place value, the four operations, the first fractions, perimeter and
 * area. This file is everything else Grade 3 actually teaches: the strategies
 * that turn multiplication facts into methods, the number line as a home for
 * fractions, the measured world (time, mass, capacity), the first data
 * displays, and shape as a set of attributes rather than a silhouette.
 *
 * Split out rather than merged into `elementary.ts` for the same reason
 * `grade-eight.ts` exists: the band is a cohort, and a reviewer asking "what
 * does Grade 3 cover" should be able to read one file.
 *
 * Every prerequisite here points at something already available, so nothing in
 * this file is reachable-but-unstudiable.
 */
export const GRADE_THREE_CONCEPTS: Concept[] = [
  /* ------------------------------------------- multiplication and division */
  {
    id: 'multiplication-properties',
    title: 'Properties of Multiplication',
    summary: 'Order does not matter, grouping does not matter, and a hard fact can be split into two easy ones.',
    strand: 'arithmetic',
    levels: ['grade-3'],
    prerequisites: ['multiplication-facts'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'multiply-by-tens',
    title: 'Multiplying by Tens',
    summary: 'Multiplying by 30 is multiplying by 3 and then by 10 — the digits stay, the places shift.',
    strand: 'arithmetic',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multiplication-facts', 'place-value'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'fact-families',
    title: 'Fact Families',
    summary: 'Multiplication and division are the same fact read in two directions.',
    strand: 'arithmetic',
    levels: ['grade-3'],
    prerequisites: ['division-meaning'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'number-patterns',
    title: 'Patterns in Numbers',
    summary: 'A sequence has a rule, and the rule predicts a term you have not written yet.',
    strand: 'arithmetic',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multiplication-facts'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'two-step-problems',
    title: 'Two-Step Word Problems',
    summary: 'Some questions need an answer before they can be answered — find the middle number first.',
    strand: 'arithmetic',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multiplication-facts', 'multi-digit-subtraction'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ---------------------------------------------------------- fractions */
  {
    id: 'fractions-on-number-line',
    title: 'Fractions on the Number Line',
    summary: 'A fraction is not only a piece of a cake — it is a number, with a place of its own.',
    strand: 'fractions',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['fraction-basics'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'whole-number-fractions',
    title: 'Fractions and Whole Numbers',
    summary: 'Every whole number is a fraction too, and some fractions land exactly on one.',
    strand: 'fractions',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['fractions-on-number-line'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* -------------------------------------------------------- measurement */
  {
    id: 'telling-time',
    title: 'Telling the Time',
    summary: 'Two hands, one dial, two different rules for reading them.',
    strand: 'measurement',
    levels: ['grade-2', 'grade-3'],
    prerequisites: ['multiplication-facts'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'elapsed-time',
    title: 'Elapsed Time',
    summary: 'How long something took — counting in sixties, not in tens.',
    strand: 'measurement',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['telling-time', 'multi-digit-subtraction'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'mass-and-capacity',
    title: 'Mass and Liquid Volume',
    summary: 'Grams and kilograms, millilitres and litres — and why the unit is part of the answer.',
    strand: 'measurement',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multi-digit-addition', 'multiply-by-tens'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ---------------------------------------------------------- statistics */
  {
    id: 'scaled-graphs',
    title: 'Picture and Bar Graphs',
    summary: 'One symbol can stand for five things — read the key before you read the graph.',
    strand: 'statistics',
    levels: ['grade-3'],
    prerequisites: ['multiplication-facts'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'line-plots',
    title: 'Line Plots',
    summary: 'Stack one dot per measurement above its value, and the shape of the data appears.',
    strand: 'statistics',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['scaled-graphs'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ------------------------------------------------------------ geometry */
  {
    id: 'shape-attributes',
    title: 'Shapes and Their Attributes',
    summary: 'A shape is named by what is true of it, not by what it looks like from where you are standing.',
    strand: 'geometry',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['perimeter'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'equal-shares',
    title: 'Equal Shares of a Shape',
    summary: 'Cut a shape into equal parts and each part has a name — and the parts must really be equal.',
    strand: 'geometry',
    levels: ['grade-3'],
    prerequisites: ['fraction-basics'],
    status: 'available',
    estimatedMinutes: 20,
  },
  {
    id: 'area-composite',
    title: 'Area of Composite Figures',
    summary: 'A shape that is not a rectangle is usually rectangles in disguise.',
    strand: 'geometry',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['area-rectangles', 'multiplication-properties'],
    status: 'available',
    estimatedMinutes: 30,
  },
];
